'use client';
import React, { useEffect, useRef, useState } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { getSiteConfig } from '../../../../siteConfig';
import FeedFilter from './feedFilter';
import Proposals from './proposals';
import {
  getUrlParameter,
  selectPeriod,
  trackError,
  trackFeedItems,
} from './feedUtility';
import { combinePeriodsOnWeek } from '../../../utils/dateTime';
import { getCachedItem, setCachedItem } from '../../../utils/cacheUtil';
import ProposalCard from './proposalCard';
import PlusIcon from '../../atoms/icons/plus';

const Feed = ({ blok }) => {
  const {
    airport: initAirport,
    chartertype: initCharterType,
    packageIds: initPackageIds,
    travel_type: initTravelType,
    period_start,
    period_end,
    selected_period_start,
    selected_period_end,
    default_all_destinations,
    default_destination,
    sort_order,
    persons_adults = 2,
    persons_children = 0,
    show_alternative_flights = false,
    sort_order: initSortOrder = 'lowestPrice',
    offerCode,
  } = blok;

  const { ethelBookingURL, ethelMarket } = getSiteConfig();

  const initDestination = default_destination
    ? default_destination
    : default_all_destinations
    ? '*'
    : null;

  const [modalProposal, setModalProposal] = useState(null);
  const [realModalProposal, setRealModalProposal] = useState(null);

  useEffect(() => {
    if (modalProposal) {
      setRealModalProposal(modalProposal);
    } else {
      setRealModalProposal(null);
    }
  }, [modalProposal]);

  const getPreferredAirport = () => {
    if (typeof window !== 'undefined') {
      const airportParam = getUrlParameter('airport');
      if (airportParam) {
        return airportParam;
      }

      let cachedData = getCachedItem(`preferred-airport-${blok._uid}`);
      if (cachedData) {
        return cachedData;
      } else if (initAirport) return initAirport;
    } else return null;
  };

  const season = getUrlParameter('season');
  const chartertype = getUrlParameter('charterTypeId') || initCharterType;
  const packageIds = getUrlParameter('pIds') || initPackageIds;
  const travel_type = getUrlParameter('travelType') || initTravelType;
  const [sortOrder, setSortOrder] = useState(
    getUrlParameter('sortOrder') || initSortOrder,
  );

  const [userInteraction, setUserInteraction] = useState(false);
  const [airports, setAirports] = useState([]);
  const [airport, setAirport] = useState(
    getUrlParameter('airport') || getPreferredAirport(),
  );

  const hotel = getUrlParameter('hotelId');

  useEffect(() => {
    setCachedItem(`preferred-airport-${blok._uid}`, airport);
  }, [airport, blok._uid]);

  const [destinations, setDestinations] = useState([]);

  const getDestination = () => {
    const destinationParam = getUrlParameter('destination');
    if (destinationParam) {
      return destinationParam;
    } else if (initDestination) return initDestination;
    else return null;
  };

  const [destination, setDestination] = useState(getDestination());
  const [periods, setPeriods] = useState([]);

  const getInitPeriod = () => {
    //get the period from the url if it exists
    //if not, check if the selected_period_start and selected_period_end exists
    //if cached data exists, use it
    //else use selected_period_start and selected_period_end
    //else use the first period in the list
    let p = {};
    if (typeof window !== 'undefined') {
      const periodParam = getUrlParameter('date');
      if (periodParam) {
        const out = new Date(Date.parse(periodParam.replace(' ', '+')));

        let home = new Date(Date.parse(periodParam.replace(' ', '+')));

        const homeDateParam = getUrlParameter('home');
        if (homeDateParam) {
          home = new Date(Date.parse(homeDateParam.replace(' ', '+')));
        } else {
          home.setDate(new Date(periodParam).getDate() + 7);
        }

        p = {
          out: out.toISOString(),
          home: home.toISOString(),
        };
      } else {
        let cachedData = getCachedItem(`period-${blok._uid}`);
        if (cachedData && cachedData !== 'undefined' && cachedData.out) {
          p = cachedData;
        } else if (selected_period_start && selected_period_end) {
          p = {
            out: new Date(selected_period_start).toISOString(),
            outFrom: new Date(selected_period_start).toISOString(),
            home: new Date(selected_period_end).toISOString(),
            homeTo: new Date(selected_period_end).toISOString(),
          };
        } else if (periods && periods.length > 0) {
          p = periods[0];
        }
      }

      return p;
    }

    return p;
  };

  const [period, setPeriod] = useState(getInitPeriod());

  useEffect(() => {
    setCachedItem(`period-${blok._uid}`, period);
  }, [period]);

  const [proposals, setProposals] = useState(null);

  const [initialSearch, setInitialSearch] = useState(true);
  const [quickbookData, setQuickbookData] = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(true);
  const [loadingProposals, setLoadingProposals] = useState(true);

  useEffect(() => {
    if (quickbookData) {
      if (quickbookData.airports) {
        setAirports(quickbookData.airports);
      } else if (quickbookData.destinationGroups) {
        setDestinations(quickbookData.destinationGroups);
        const allHotelPeriods = quickbookData.destinationGroups.map(
          (d) => d.periods,
        );
        const combinedPeriods = combinePeriodsOnWeek(allHotelPeriods.flat());
        const findSelectedPeriod = selectPeriod(combinedPeriods, period);
        setPeriod(findSelectedPeriod);
        setPeriods(combinedPeriods);
      }
      setLoadingFilter(false);
    }
  }, [quickbookData]);

  const getRooms = () => {
    const children = Number(persons_children);
    let rooms = [
      {
        adults: Number(persons_adults),
        children: children,
        childrenAges: Array(children).fill(13),
      },
    ];

    if (typeof window !== 'undefined') {
      let cachedData = localStorage.getItem(`search-rooms-${blok._uid}`);

      const roomsSearchParam = getUrlParameter('rooms');

      if (roomsSearchParam) {
        const parsedRooms = JSON.parse(roomsSearchParam);
        rooms = parsedRooms.map((room) => {
          const childrenAges = room.filter((age) => age !== null);
          return {
            adults: room.length - childrenAges.length,
            children: childrenAges.length,
            childrenAges: childrenAges,
          };
        });
      } else {
        const roomsParam = getUrlParameter('persons');
        if (roomsParam) {
          rooms[0].adults = Number(roomsParam);
        } else if (cachedData) {
          if (cachedData && cachedData !== 'undefined') {
            rooms = JSON.parse(cachedData);
          }
        }
      }
    }

    return rooms;
  };

  const [rooms, setRooms] = useState(getRooms());

  useEffect(() => {
    localStorage.setItem(`search-rooms-${blok._uid}`, JSON.stringify(rooms));
  }, [rooms]);

  const filterProps = {
    airport,
    setAirport,
    airports,
    travelType: travel_type,
    destination,
    setDestination,
    destinations,
    rooms,
    setRooms,
    loadingFilter,
    period,
    periods,
    setPeriod,
    noOfProposals: proposals?.periods?.length || 0,
    setUserInteraction: setUserInteraction,
  };

  const packageIdsArray =
    packageIds
      ?.split(/\r?\n|\r|\n/g)
      .filter((p) => p)
      .map((p, i) => `&cmsPackageIds[${i}]=` + p) || '';

  useEffect(() => {
    setLoadingFilter(true);
    const fetchQuickbookData = async (travel_type) => {
      const packageIdsSplit =
        packageIds && packageIds.length > 0
          ? packageIds.split(/\r?\n|\r|\n/g)
          : null;

      const packageIdsString = packageIdsSplit
        ? packageIdsSplit
            .filter((p) => p)
            .map((p, index) => '&packageIds[' + index + ']=' + p)
        : '';

      const pStart = period_start ? new Date(period_start) : new Date();
      const pEnd = period_end
        ? new Date(period_end)
        : new Date(new Date().setFullYear(new Date().getFullYear() + 1));

      const quikbookUrl = `${ethelBookingURL}/api/v1/query/travelBooking/quikbook?marketId=${ethelMarket}&travelType=${travel_type}&charterTypeId=${chartertype}&travelDateFrom=${encodeURIComponent(
        pStart.toISOString().split('T')[0],
      )}T00%3A00%3A00%2B01%3A00&travelDateTo=${encodeURIComponent(
        pEnd.toISOString().split('T')[0],
      )}T00%3A00%3A00%2B01%3A00${
        packageIdsString && packageIdsString.length > 0
          ? packageIdsString.join('')
          : ''
      }`;

      const response = await fetch(quikbookUrl);
      const res = await response.json();

      if (res.error) {
        trackError(res.error, quikbookUrl);
        setLoadingFilter(false);
        return;
      } else return res.data;
    };
    //};

    const loadQBData = async () => {
      const cacheKey = `travelData-${ethelMarket}-${travel_type}-${season}`;

      let cachedData = season ? getCachedItem(cacheKey) : null;

      if (!cachedData) {
        cachedData = await fetchQuickbookData(travel_type);
        if (season) {
          setCachedItem(cacheKey, cachedData);
        }
      }
      setQuickbookData(cachedData);

      setLoadingFilter(false);
    };

    loadQBData();
  }, []);

  useEffect(() => {
    const preferredAirport = getPreferredAirport();

    if (airports && airports.length > 0) {
      let airport = airports[0].airportId;

      if (preferredAirport) {
        const preferredAirportData = airports.find(
          (a) => a.airportId === preferredAirport,
        );

        if (preferredAirportData) {
          airport = preferredAirportData.airportId;
        }
      }

      setAirport(airport);
    }
  }, [airports]);

  const getAirportObject = () => {
    return airports?.find((a) => a.airportId === airport);
  };

  useEffect(() => {
    const apt = getAirportObject();

    if (apt) {
      const destinationGroups = apt?.destinationGroups;

      setDestinations(destinationGroups);
      if (getUrlParameter('destination')) {
        setDestination(getUrlParameter('destination'));
      } else if (default_all_destinations) {
        setDestination('*');

        const combinedPeriods = combinePeriodsOnWeek(apt.periods);
        setPeriods(combinedPeriods);
      } else {
        //check if initial destination exists in any destination group, needed when changing charter type
        const destinationGroupForInitDestination = destinationGroups.find(
          (group) =>
            group.destinations.some((d) => d.id === initDestination) ||
            group.id === initDestination,
        );

        if (initDestination && destinationGroupForInitDestination) {
          setDestination(initDestination);
        } else if (destinationGroups && destinationGroups.length > 0) {
          setDestination(destinationGroups[0].destinations[0].id);
        }
      }
    }
  }, [airport, airports]);

  //set destinations when airport changes
  useEffect(() => {
    if (airports && airports.length > 0 && airport) {
      const airp = airports.find((a) => a.airportId === airport);

      setDestinations(airp.destinationGroups);
      if (destination === '*') {
        const airportPeriods = quickbookData.airports.find(
          (a) => a.airportId === airport,
        ).periods;

        const combinedPeriods =
          season === 'christmas'
            ? airportPeriods
            : combinePeriodsOnWeek(airportPeriods);

        setPeriods(combinedPeriods);
        setPeriod(combinedPeriods[0]);
      }
    }
  }, [airport]);

  //set periods when destination changes
  useEffect(() => {
    if (destinations && destinations.length > 0) {
      if (destination === '*' || !destination) {
        if (travel_type === 'Hotel') {
          const allHotelPeriods = destinations.map((d) => d.periods).flat();

          const combinedPeriods = combinePeriodsOnWeek(allHotelPeriods);

          const foundSelectedPeriod = selectPeriod(combinedPeriods, period);
          setPeriods(combinedPeriods);
          setPeriod(foundSelectedPeriod);
        } else {
          const airportPeriods = quickbookData.airports.find(
            (a) => a.airportId === airport,
          ).periods;

          const combinedPeriods =
            season === 'christmas'
              ? airportPeriods
              : combinePeriodsOnWeek(airportPeriods);
          setPeriods(combinedPeriods);

          const p = selectPeriod(combinedPeriods, period);
          setPeriod(p);
        }
      } else {
        const destinationGroup = destinations.find(
          (group) =>
            group.destinations.some((d) => d.id === destination) ||
            group.id === destination,
        );
        if (destinationGroup) {
          const dest = destinationGroup.destinations.find(
            (d) => d.id === destination,
          );

          let periods = [];
          if (dest) {
            periods = dest.periods;
          } else {
            periods = destinationGroup.periods;
          }

          const combinedPeriods =
            season === 'christmas' ? periods : combinePeriodsOnWeek(periods);
          setPeriods(combinedPeriods);

          const p = selectPeriod(periods, period);
          setPeriod(p);
        }
      }
    }
  }, [destination, destinations]);

  const ref = useRef(null);

  useEffect(() => {
    if (initialSearch) {
      if (proposals !== null) {
        setInitialSearch(false);
      }
    } else {
      if (userInteraction) {
        ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [proposals]);

  const getProposalData = async () => {
    setLoadingProposals(true);

    //https://booking.alpresor.se/api/v1/query/travelBooking/proposals?compactMode=true&airportId=ARN&charterTypeId=bb62adda-a409-4f14-85f5-4cddf2d6a11f&outwardDateFrom=2024-12-21T00%3A00%3A00%2B01%3A00&outwardDateTo=2024-12-21T23%3A00%3A00%2B01%3A00&homewardDateFrom=2024-12-28T00%3A00%3A00%2B01%3A00&homewardDateTo=2024-12-28T23%3A00%3A00%2B01%3A00&numberOfTravelers=2&travelerAge%5B0%5D=40&travelerAge%5B1%5D=40&travelersPerRoom%5B0%5D=2&sortOrder=lowestPrice&cmsPackageIds%5B0%5D=1b0b4872-9121-41a2-a6a2-8e4a9dc8af2e&showAlternativeFlights=false

    let numberOfTravelers = 0;

    let travelerAges = [];
    let travellersPerRoom = [];
    let adults = 0;
    let children = 0;
    let infants = 0;

    rooms.forEach((room) => {
      numberOfTravelers += room.adults + room.children;

      for (let i = 0; i < room.adults; i++) {
        travelerAges.push(40);
        adults++;
      }
      for (let i = 0; i < room.children; i++) {
        travelerAges.push(room.childrenAges[i]);
        if (room.childrenAges[i] < 2) infants++;
        else children++;
      }
      travellersPerRoom.push(room.adults + room.children);
    });

    if (numberOfTravelers === 0 || (adults === 0 && infants > 0)) {
      setLoadingProposals(false);
      return;
    }

    if (period?.home && period?.out) {
      const destinationId =
        destination && destination !== '*'
          ? `&destinationId=${destination}`
          : '';

      const airportString =
        travel_type !== 'Hotel' ? `&airportId=${airport}` : '';

      /*      const outFromDate = new Date(period.out);
      //outFromDate.setHours(2, 0, 0, 0);
      let outFromString = `${outFromDate.toISOString().split('.')[0]}+01:00`;
*/
      let outTo = new Date(period.out);
      outTo.setDate(outTo.getDate() + 1);
      outTo.setHours(23, 0, 0);
      const outToString = `${outTo.toISOString().split('.')[0]}+01:00`;

      let homeTo = new Date(period.home);
      homeTo.setDate(homeTo.getDate() + 1);
      homeTo.setHours(23, 59, 59);

      const homeToString = homeTo.toISOString().replace('T00', 'T23');

      const proposalsQuery = `${ethelBookingURL}/api/v1/query/travelBooking/proposals?compactMode=false${airportString}${destinationId}&charterTypeId=${chartertype}&outwardDateFrom=${encodeURIComponent(
        period.out,
      )}&outwardDateTo=${encodeURIComponent(
        period.outTo || period.out,
      )}&homewardDateFrom=${encodeURIComponent(
        period.homeFrom || period.home,
      )}&homewardDateTo=${encodeURIComponent(
        homeToString,
      )}&numberOfTravelers=${numberOfTravelers}&${travelerAges
        .map((t, i) => encodeURI('travelerAge[' + i + ']=' + t))
        .join('&')}&${travellersPerRoom
        .map((t, i) => encodeURI('travelersPerRoom[' + i + ']=' + t))
        .join('&')}${
        sortOrder === 'recommended' || !sortOrder
          ? ''
          : `&sortOrder=${sortOrder}`
      }${packageIdsArray.join('')}&showAlternativeFlights=${
        show_alternative_flights || travel_type === 'Flight'
      }${packageIdsArray.join('')}${
        offerCode ? `&offerCode=${offerCode}` : ''
      }`;

      const response = await fetch(proposalsQuery);
      const { data: proposalsData, error } = await response.json();

      if (proposalsData?.periods?.length > 0 && travel_type !== 'Flight') {
        const groupedProposals = proposalsData.periods[0]?.proposals.reduce(
          (acc, proposal) => {
            const hotelId = proposal.hotel.hotelId;
            if (!acc[hotelId]) {
              acc[hotelId] = { ...proposal, alternativeProposals: [] };
            } else {
              acc[hotelId].alternativeProposals.push(proposal);
            }
            return acc;
          },
          {},
        );

        const groupedProposalsArray = Object.values(groupedProposals);

        proposalsData.periods[0].proposals = groupedProposalsArray;
      }

      if (error) {
        trackError(error, proposalsQuery);
        setLoadingProposals(false);
      } else {
        setProposals(proposalsData);
        setLoadingProposals(false);
        if (proposalsData?.periods?.length > 0) {
          trackFeedItems(proposalsData.periods[0].proposals, {
            airport: airport || 'Hotel Only',
            destination: destination,
          });
        }
      }
    }
  };
  useEffect(() => {
    getProposalData();
  }, [destination, period, rooms, travel_type]);

  return (
    <div
      {...storyblokEditable(blok)}
      key={blok._uid}
      id="feed"
      className={`col-span-12 flex flex-col mt-12 scroll-mt-20 ${
        modalProposal ? 'z-110' : 'z-50'
      } relative`}
      ref={ref}
    >
      <div
        className={`${
          modalProposal ? 'flex' : 'hidden'
        } fixed inset-0 bg-black bg-opacity-80 transition-opacity z-110 items-center align-center flex-wrap justify-center`}
        onClick={() => {
          setModalProposal(null);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            {
              setModalProposal(null);
            }
          }
        }}
      >
        <div
          className={`z-90 w-full max-w-[98%] lg:max-w-screen-lg drop-shadow-2xl`}
        >
          {/* Close button */}
        </div>
      </div>
      {realModalProposal && (
        <div className="z-110 w-full max-w-[98%] lg:max-w-screen-xs drop-shadow-2xl fixed left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 grid gap-2 md:gap-4 grid-cols-12">
          <div className="flex w-full lg:w-auto absolute right-0 -top-8 z-10 justify-end px-8 max-w-screen-xs lg:max-w-screen-xs left-1/2 lg:left-auto -translate-x-1/2 lg:transform-none drop-shadow">
            <button
              onClick={() => {
                setModalProposal(null);
              }}
              className="bg-yellow-60 rounded-full px-2 py-2 rotate-45"
            >
              <PlusIcon className="w-6 h-6" />
            </button>
          </div>

          <ProposalCard
            proposal={realModalProposal.proposal}
            charterTypeId={realModalProposal.charterTypeId}
            rooms={realModalProposal.rooms}
            offerCode={realModalProposal.offerCode}
            setModalProposal={setModalProposal}
            layout={'grid'}
            isModal={true}
          />
        </div>
      )}
      <div className="flex w-full relative">
        {blok.show_title && (
          <h1 className="px-4 md:px-8 mt-12 mb-4 text-lg md:text-xl">
            {quickbookData?.description}
          </h1>
        )}
      </div>
      <FeedFilter props={{ ...filterProps }} />
      <Proposals
        data={proposals}
        charterTypeId={chartertype}
        loading={loadingProposals}
        period={period}
        setPeriod={setPeriod}
        periods={periods}
        hotel={hotel}
        destination={destination}
        destinations={destinations}
        sortOrder={sortOrder}
        offerCode={offerCode}
        setModalProposal={setModalProposal}
      />
    </div>
  );
};

export default Feed;
